import {
  currentSpaceAtom,
  currentUserAtom,
  spaceListAtom,
} from "../jotai/atoms";
import { useAtom } from "jotai";
import React, { useEffect, useState } from "react";
import { LOCAL_STORAGE_KEYS } from "../consts";
import { useParams, Link } from "react-router-dom";
import Loader from "../components/Loader";
import Presence from "../av/Presence";
import { styled } from "../stitches.config";
import { InlineButton } from "../components/Button";
import { RoomServiceProvider } from "@roomservice/react";
import { useRoomClient } from "../roomservice/utils";
import SpaceCreationLoader from "./loaders/SpaceCreationLoader";
import { DialogTakeover, Message } from "../components/Dialog";
import ArchivedLoader from "./loaders/ArchivedLoader";
import Telemetry from "../av/Telemetry";

export default function SpaceLoader({ children }: React.PropsWithChildren<{}>) {
  const [currentUser] = useAtom(currentUserAtom);
  const [currentSpace] = useAtom(currentSpaceAtom);
  const spaceError = useLoadSpace();

  // Error
  if (spaceError) {
    return (
      <ErrorMessage>
        Invalid space. Please try{" "}
        <StyledLink to="/">creating new one</StyledLink>
      </ErrorMessage>
    );
  }

  // Loading
  if (!currentSpace) {
    return <Loader>Loading space...</Loader>;
  }

  return (
    <RoomServiceProvider
      clientParameters={{
        auth: `/api/roomservice/?user=${currentUser!.id}`,
      }}
    >
      <RoomServiceLoader>
        <SpaceCreationLoader>
          <ArchivedLoader>
            {children}
            <Presence />
            <Telemetry />
          </ArchivedLoader>
        </SpaceCreationLoader>
      </RoomServiceLoader>
    </RoomServiceProvider>
  );
}
const StyledLink = styled(Link, {
  color: "$linkBlue",
  textDecoration: "none",
});

function RoomServiceLoader({ children }: React.PropsWithChildren<{}>) {
  // activate room service auth for current space as soon as we can to minimize initial load time
  useRoomClient();
  return <> {children} </>;
}

function useLoadSpace() {
  const [, setCurrentSpace] = useAtom(currentSpaceAtom);
  const { spaceId } = useParams<{ spaceId: string }>();
  const [error, setError] = useState<boolean>(false);
  const [, setSpaceList] = useAtom(spaceListAtom);

  useEffect(() => {
    if (!spaceId || spaceId.length < 2) {
      setError(true);
      return;
    }
    setCurrentSpace({
      id: spaceId,
    });
    const spaces = window.localStorage.getItem(LOCAL_STORAGE_KEYS.HISTORY);
    // Filter out old instances of current space
    const newSpaces = spaces
      ? [spaceId, ...JSON.parse(spaces).filter((id: string) => id !== spaceId)]
      : [spaceId];
    window.localStorage.setItem(
      LOCAL_STORAGE_KEYS.HISTORY,
      JSON.stringify(newSpaces)
    );
    setSpaceList(newSpaces);
  }, [spaceId, setCurrentSpace, setSpaceList]);

  return error;
}

function ErrorMessage({ children }: React.PropsWithChildren<{}>) {
  return (
    <DialogTakeover title="Oops!">
      <Message>
        {children || (
          <>
            An unknown error has occurred. Please report a bug (bottom left),
            and then try{" "}
            <InlineButton
              aria-label="reload"
              onClick={() => window.location.reload()}
            >
              reloading this space
            </InlineButton>
          </>
        )}
      </Message>
    </DialogTakeover>
  );
}
